// ============================================================================
// Auth Hook — Cognito authentication context with token persistence & refresh
// ============================================================================

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { config } from '../services/config';
import { api } from '../services/api';

// ── Types ───────────────────────────────────────────────────────────

interface AuthUser {
  email: string;
  userId: string;
  groups: string[];
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  isCognitoConfigured: boolean;
  user: AuthUser | null;
  error: string | null;
  needsNewPassword: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => void;
  getValidToken: () => Promise<string | null>;
  isAdmin: boolean;
  isEditor: boolean;
  role: 'admin' | 'editor' | 'none';
}

const AuthContext = createContext<AuthContextType | null>(null);

// ── Cognito Helpers ─────────────────────────────────────────────────

function isCognitoConfigured(): boolean {
  return !!(config.cognitoUserPoolId && config.cognitoClientId);
}

function getUserPool(): CognitoUserPool | null {
  if (!isCognitoConfigured()) return null;
  return new CognitoUserPool({
    UserPoolId: config.cognitoUserPoolId,
    ClientId: config.cognitoClientId,
  });
}

/**
 * Extract user info from a valid Cognito session.
 */
function extractUser(session: CognitoUserSession): AuthUser {
  const payload = session.getIdToken().decodePayload();
  const groupsClaim = payload['cognito:groups'];
  let groups: string[] = [];
  if (Array.isArray(groupsClaim)) {
    groups = groupsClaim as string[];
  } else if (typeof groupsClaim === 'string') {
    groups = groupsClaim.split(',');
  }
  return {
    email: (payload['email'] as string) || '',
    userId: (payload['sub'] as string) || '',
    groups,
  };
}

/**
 * Check if a token will expire within the given number of minutes.
 */
function tokenExpiresWithin(session: CognitoUserSession, minutes: number): boolean {
  const expiration = session.getIdToken().getExpiration(); // Unix seconds
  const now = Math.floor(Date.now() / 1000);
  return expiration - now < minutes * 60;
}

// ── Provider ────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    isCognitoConfigured: isCognitoConfigured(),
    user: null,
    error: null,
    needsNewPassword: false,
  });

  // Store the CognitoUser for new-password-required flow
  const pendingUserRef = useRef<CognitoUser | null>(null);
  // Store refresh timer reference
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Get a valid token, refreshing if necessary.
   * This is the function the API client uses before every request.
   */
  const getValidToken = useCallback((): Promise<string | null> => {
    return new Promise((resolve) => {
      const pool = getUserPool();
      if (!pool) {
        resolve(null);
        return;
      }

      const cognitoUser = pool.getCurrentUser();
      if (!cognitoUser) {
        resolve(null);
        return;
      }

      // getSession will automatically use the refresh token if the
      // access/id tokens are expired. The amazon-cognito-identity-js SDK
      // handles this transparently via the stored refresh token in localStorage.
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        // If token expires within 5 minutes, force a refresh
        if (tokenExpiresWithin(session, 5)) {
          const refreshToken = session.getRefreshToken();
          cognitoUser.refreshSession(refreshToken, (refreshErr: Error | null, newSession: CognitoUserSession | null) => {
            if (refreshErr || !newSession) {
              resolve(null);
              return;
            }
            resolve(newSession.getIdToken().getJwtToken());
          });
          return;
        }

        resolve(session.getIdToken().getJwtToken());
      });
    });
  }, []);

  /**
   * Schedule a background token refresh before the current token expires.
   */
  const scheduleTokenRefresh = useCallback((session: CognitoUserSession) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const expiration = session.getIdToken().getExpiration();
    const now = Math.floor(Date.now() / 1000);
    // Refresh 5 minutes before expiry
    const refreshInMs = Math.max((expiration - now - 300) * 1000, 60000);

    refreshTimerRef.current = setTimeout(async () => {
      const token = await getValidToken();
      if (token) {
        // Re-extract session to schedule next refresh
        const pool = getUserPool();
        const user = pool?.getCurrentUser();
        if (user) {
          user.getSession((err: Error | null, newSession: CognitoUserSession | null) => {
            if (!err && newSession?.isValid()) {
              scheduleTokenRefresh(newSession);
            }
          });
        }
      }
    }, refreshInMs);
  }, [getValidToken]);

  // Wire up the token provider for the API client
  useEffect(() => {
    api.setTokenProvider(getValidToken);
  }, [getValidToken]);

  // Check for existing session on mount (from Cognito SDK's localStorage)
  useEffect(() => {
    const pool = getUserPool();
    if (!pool) {
      // Cognito is NOT configured — do not auto-authenticate
      setState({
        isAuthenticated: false,
        isLoading: false,
        isCognitoConfigured: false,
        user: null,
        error: null,
        needsNewPassword: false,
      });
      return;
    }

    const cognitoUser = pool.getCurrentUser();
    if (!cognitoUser) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    // getSession auto-refreshes expired tokens using the stored refresh token
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      const user = extractUser(session);
      scheduleTokenRefresh(session);

      setState({
        isAuthenticated: true,
        isLoading: false,
        isCognitoConfigured: true,
        user,
        error: null,
        needsNewPassword: false,
      });
    });
  }, [scheduleTokenRefresh]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  /**
   * Log in with email and password.
   */
  const login = useCallback(async (email: string, password: string) => {
    const pool = getUserPool();
    if (!pool) {
      setState((prev) => ({
        ...prev,
        error: 'Authentication is not configured. Please set up Cognito credentials.',
      }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null, needsNewPassword: false }));

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: pool,
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    return new Promise<void>((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const user = extractUser(session);
          scheduleTokenRefresh(session);

          setState({
            isAuthenticated: true,
            isLoading: false,
            isCognitoConfigured: true,
            user,
            error: null,
            needsNewPassword: false,
          });
          resolve();
        },
        onFailure: (err) => {
          setState({
            isAuthenticated: false,
            isLoading: false,
            isCognitoConfigured: true,
            user: null,
            error: err.message || 'Authentication failed',
            needsNewPassword: false,
          });
          reject(new Error(err.message));
        },
        newPasswordRequired: (_userAttributes) => {
          // First login with admin-created temp password
          pendingUserRef.current = cognitoUser;
          setState({
            isAuthenticated: false,
            isLoading: false,
            isCognitoConfigured: true,
            user: null,
            error: null,
            needsNewPassword: true,
          });
          resolve(); // Don't reject — UI will show new password form
        },
      });
    });
  }, [scheduleTokenRefresh]);

  /**
   * Complete the new password required challenge (first login).
   */
  const completeNewPassword = useCallback(async (newPassword: string) => {
    const cognitoUser = pendingUserRef.current;
    if (!cognitoUser) {
      setState((prev) => ({ ...prev, error: 'No pending password challenge.' }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    return new Promise<void>((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (session) => {
          pendingUserRef.current = null;
          const user = extractUser(session);
          scheduleTokenRefresh(session);

          setState({
            isAuthenticated: true,
            isLoading: false,
            isCognitoConfigured: true,
            user,
            error: null,
            needsNewPassword: false,
          });
          resolve();
        },
        onFailure: (err) => {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: err.message || 'Failed to set new password',
          }));
          reject(new Error(err.message));
        },
      });
    });
  }, [scheduleTokenRefresh]);

  /**
   * Log out — clear all tokens from localStorage and state.
   */
  const logout = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const pool = getUserPool();
    if (pool) {
      const cognitoUser = pool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
    }
    pendingUserRef.current = null;

    setState({
      isAuthenticated: false,
      isLoading: false,
      isCognitoConfigured: isCognitoConfigured(),
      user: null,
      error: null,
      needsNewPassword: false,
    });
  }, []);

  const groups = state.user?.groups || [];
  const isAdminUser = groups.includes('admin');
  const isEditorUser = groups.includes('editor');
  const role: 'admin' | 'editor' | 'none' = isAdminUser ? 'admin' : isEditorUser ? 'editor' : 'none';

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      completeNewPassword,
      logout,
      getValidToken,
      isAdmin: isAdminUser,
      isEditor: isEditorUser,
      role,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
